import { computed, inject, ref, watch, watchEffect } from 'vue'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreTitleLogs } from '@/stores/storeTitleLogs'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreProfile } from '@/stores/storeProfile'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { useStoreModals } from '@/stores/storeModals'
import { useGuestActions } from '@/composables/useGuestActions'

let storeModals = null // useStoreModals()
let storeTitlesUser = null //useStoreTitlesUser()
let storeTitleLogs = null
let storeLists = null
let storeReviews = null
let storeAuth = null
let storeProfile = null

export function useTitleActions(props) {
  const quickStateSeen = ref(false)
  const quickStateFaved = ref(false)
  const quickStateWatchlist = ref(false)
  const quickStateInProgress = ref(false)
  const showAddToListSheet = ref(false)
  const showReviewSheet = ref(false)
  const addToListMetadata = ref(null)
  const { requireAuthFor } = useGuestActions()

  // Check if user prefers fullscreen modals
  const fullscreenModals = computed(() => {
    if (storeProfile === null) {
      storeProfile = useStoreProfile()
    }
    return storeProfile.getCurrentUserProfile?.feature_flags?.fullscreen_modals === true
  })

  // Log system is always enabled
  const usesLogSystem = computed(() => true)

  // Get derived state from log system, falling back to system list membership
  // so legacy items without title_logs entries still show the correct badge
  const derivedState = computed(() => {
    if (!usesLogSystem.value) return null
    if (storeTitleLogs === null) {
      storeTitleLogs = useStoreTitleLogs()
    }
    const tmdbId = props.value?.title?.id
    const tmdbType = props.value?.title?.media_type
    if (!tmdbId || !tmdbType) return null

    const logState = storeTitleLogs.getDerivedStateByTmdb(tmdbId, tmdbType)
    if (logState) return logState

    // Fallback: check if title is in a system list (legacy items without logs)
    if (storeLists === null) {
      storeLists = useStoreLists()
    }
    const systemTypes = { watchlist: 'watchlist', seen: 'seen' }
    for (const [sysType, state] of Object.entries(systemTypes)) {
      const list = storeLists.getSystemListByType(sysType)
      if (!list?.id) continue
      const inList = storeLists.items.some(
        (item) =>
          item.list_id === list.id &&
          item.titles?.tmdb_id == tmdbId &&
          item.titles?.tmdb_type == tmdbType
      )
      if (inList) return state
    }

    return null
  })

  // Doc 27: state of the active history log (seen | in_progress | dropped),
  // falling back to seen system-list membership for legacy items without logs
  const historyState = computed(() => {
    if (!usesLogSystem.value) return null
    if (storeTitleLogs === null) {
      storeTitleLogs = useStoreTitleLogs()
    }
    const tmdbId = props.value?.title?.id
    const tmdbType = props.value?.title?.media_type
    if (!tmdbId || !tmdbType) return null

    const logState = storeTitleLogs.getHistoryStateByTmdb(tmdbId, tmdbType)
    if (logState) return logState

    if (storeLists === null) {
      storeLists = useStoreLists()
    }
    const seenList = storeLists.getSystemListByType('seen')
    if (seenList?.id) {
      const inList = storeLists.items.some(
        (item) =>
          item.list_id === seenList.id &&
          item.titles?.tmdb_id == tmdbId &&
          item.titles?.tmdb_type == tmdbType
      )
      if (inList) return 'seen'
    }

    return null
  })

  // Ensure title state is loaded on-demand (fetches from DB if not cached)
  watchEffect(() => {
    if (!usesLogSystem.value) return
    if (storeTitleLogs === null) {
      storeTitleLogs = useStoreTitleLogs()
    }
    const tmdbId = props.value?.title?.id
    const tmdbType = props.value?.title?.media_type
    if (tmdbId && tmdbType) {
      storeTitleLogs.ensureTitleState(tmdbId, tmdbType)
    }
  })

  // Context-aware modal: use card context modal if available, otherwise global
  const cardContextModal = inject('cardContextModal', null)
  const openModal = (params) => {
    if (cardContextModal?.isInCardContext) {
      cardContextModal.openModal(params)
    } else {
      if (storeModals === null) {
        storeModals = useStoreModals()
      }
      storeModals.openModal(params)
    }
  }

  const openShareModal = async () => {
    if (Capacitor.getPlatform() === 'ios') {
      try {
        // Get the most accurate title (movies have title, TV shows have name)
        const titleName =
          props.value.title?.title || props.value.title?.name || 'Spectra'

        // Get better description from overview if available
        const titleDescription =
          props.value.title?.overview ||
          props.value.title?.description ||
          'Check out this title on Spectra.'

        // Format year information if available
        let yearInfo = ''
        if (props.value.title?.release_date) {
          yearInfo = ` (${new Date(props.value.title.release_date).getFullYear()})`
        } else if (props.value.title?.first_air_date) {
          yearInfo = ` (${new Date(props.value.title.first_air_date).getFullYear()})`
        }

        // Create a more specific URL if possible
        const titleId = props.value.title?.id
        const titleType = props.value.title?.media_type || 'movie'
        const titleUrl = titleId
          ? `https://spectra.reviews/title/${titleId}/${titleType}`
          : 'https://spectra.reviews'

        await Share.share({
          title: `${titleName}${yearInfo} | Spectra`,
          text: titleDescription,
          url: titleUrl,
          dialogTitle: 'Share with your friends'
        })
      } catch (error) {
        console.error('Error sharing content:', error)
      }
    } else {
      // Fallback to modal for non-iOS platforms
      openModal({
        type: 'TitleShare',
        props: { title: props.value.title }
      })
    }
  }

  const openReviewCreateModal = () => {
    requireAuthFor.review(
      () => {
        if (fullscreenModals.value) {
          // Fullscreen ON: use global modal with Ionic card presentation
          openModal({
            type: 'ReviewCreate',
            presentation: 'fullscreen',
            props: {
              title: props.value.title,
              titleInfo: props.value.titleInfo,
              userTitleInfo: props.value.userTitleInfo,
              reviewType: props.value.type,
              tvSeason: props.value.tvSeason,
              tvEpisode: props.value.tvEpisode
            }
          })
        } else {
          // Fullscreen OFF: use Sheet-based presentation
          showReviewSheet.value = true
        }
      },
      {
        title: props.value.title?.title || props.value.title?.name,
        id: props.value.title?.id,
        type: props.value.title?.media_type
      }
    )
  }

  const openAddToListSheet = (metadata = null) => {
    requireAuthFor.addToList(
      () => {
        addToListMetadata.value = metadata
        showAddToListSheet.value = true
      },
      {
        title: props.value.title?.title || props.value.title?.name,
        id: props.value.title?.id,
        type: props.value.title?.media_type
      }
    )
  }

  const toggleSeen = async (metadata = null) => {
    requireAuthFor.markWatched(
      async () => {
        // Use log system if enabled
        if (usesLogSystem.value) {
          if (storeTitleLogs === null) {
            storeTitleLogs = useStoreTitleLogs()
          }
          const currentState = derivedState.value
          const newSeenState = currentState !== 'seen'
          quickStateSeen.value = newSeenState

          try {
            if (newSeenState) {
              await storeTitleLogs.markAsSeen(
                {
                  id: props.value.title.id,
                  type: props.value.title.media_type
                },
                metadata
              )
            } else {
              await storeTitleLogs.removeFromState(
                {
                  id: props.value.title.id,
                  type: props.value.title.media_type
                },
                'seen'
              )
            }
          } catch (error) {
            quickStateSeen.value = !newSeenState
            console.error('Failed to update watched state:', error)
          }
          return
        }

        // Legacy system
        if (storeTitlesUser === null) {
          storeTitlesUser = useStoreTitlesUser()
        }
        const newSeenState = !quickStateSeen.value
        quickStateSeen.value = newSeenState

        try {
          await storeTitlesUser.setSeen(newSeenState, {
            titleId: props.value.title.id,
            titleType: props.value.title.media_type
          })
        } catch (error) {
          // Revert optimistic update on error
          quickStateSeen.value = !newSeenState
          console.error('Failed to update watched state:', error)
        }
      },
      {
        title: props.value.title?.title || props.value.title?.name,
        id: props.value.title?.id,
        type: props.value.title?.media_type
      }
    )
  }

  const toggleFaved = async () => {
    return requireAuthFor.favorite(
      async () => {
        if (storeTitlesUser === null) {
          storeTitlesUser = useStoreTitlesUser()
        }
        const newFavedState = !quickStateFaved.value
        quickStateFaved.value = newFavedState

        try {
          await storeTitlesUser.setFaved(newFavedState, {
            titleId: props.value.title.id,
            titleType: props.value.title.media_type
          })
        } catch (error) {
          // Revert optimistic update on error
          quickStateFaved.value = !newFavedState
          console.error('Failed to update favorite state:', error)
        }
      },
      {
        title: props.value.title?.title || props.value.title?.name,
        id: props.value.title?.id,
        type: props.value.title?.media_type
      }
    )
  }

  const toggleWatchlist = async (metadata = null) => {
    requireAuthFor.addToList(
      async () => {
        // Use log system if enabled
        if (usesLogSystem.value) {
          if (storeTitleLogs === null) {
            storeTitleLogs = useStoreTitleLogs()
          }
          // Doc 27: toggles the watchlist intent only - the history state
          // (seen/in_progress/dropped) is never touched here
          const newWatchlistState = !isWatchlisted.value
          quickStateWatchlist.value = newWatchlistState

          try {
            if (newWatchlistState) {
              // Build options from metadata
              const options = {}
              if (metadata) {
                if (metadata.watchedService)
                  options.watched_service = metadata.watchedService
                if (metadata.notes) options.notes = metadata.notes
              }
              await storeTitleLogs.addToWatchlist(
                {
                  id: props.value.title.id,
                  type: props.value.title.media_type
                },
                options
              )
            } else {
              await storeTitleLogs.removeFromState(
                {
                  id: props.value.title.id,
                  type: props.value.title.media_type
                },
                'watchlist'
              )
            }
          } catch (error) {
            quickStateWatchlist.value = !newWatchlistState
            console.error('Failed to update watchlist state:', error)
          }
          return
        }

        // Legacy system
        if (storeTitlesUser === null) {
          storeTitlesUser = useStoreTitlesUser()
        }
        const newWatchlistState = !quickStateWatchlist.value
        quickStateWatchlist.value = newWatchlistState

        try {
          await storeTitlesUser.toggleWatchlist({
            titleId: props.value.title.id,
            titleType: props.value.title.media_type
          })
        } catch (error) {
          // Revert optimistic update on error
          quickStateWatchlist.value = !newWatchlistState
          console.error('Failed to update watchlist state:', error)
        }
      },
      {
        title: props.value.title?.title || props.value.title?.name,
        id: props.value.title?.id,
        type: props.value.title?.media_type
      }
    )
  }

  const toggleInProgress = async (metadata = null) => {
    requireAuthFor.markWatched(
      async () => {
        if (storeTitleLogs === null) {
          storeTitleLogs = useStoreTitleLogs()
        }
        const currentState = derivedState.value
        const newInProgressState = currentState !== 'in_progress'
        quickStateInProgress.value = newInProgressState

        try {
          if (newInProgressState) {
            await storeTitleLogs.startWatching(
              {
                id: props.value.title.id,
                type: props.value.title.media_type
              },
              metadata
            )
          } else {
            await storeTitleLogs.removeFromState(
              {
                id: props.value.title.id,
                type: props.value.title.media_type
              },
              'in_progress'
            )
          }
        } catch (error) {
          quickStateInProgress.value = !newInProgressState
          console.error('Failed to update in-progress state:', error)
        }
      },
      {
        title: props.value.title?.title || props.value.title?.name,
        id: props.value.title?.id,
        type: props.value.title?.media_type
      }
    )
  }

  // Computed states - works with both legacy and log system
  const isSeen = computed(() => {
    if (usesLogSystem.value) {
      return historyState.value === 'seen'
    }
    return quickStateSeen.value
  })

  const isFaved = computed(() => {
    return quickStateFaved.value
  })

  // Doc 27: watchlist is an intent that coexists with any history state -
  // true whenever an intent log exists, falling back to watchlist
  // system-list membership for legacy items without logs
  const isWatchlisted = computed(() => {
    if (!usesLogSystem.value) return quickStateWatchlist.value
    if (storeTitleLogs === null) {
      storeTitleLogs = useStoreTitleLogs()
    }
    const tmdbId = props.value?.title?.id
    const tmdbType = props.value?.title?.media_type
    if (!tmdbId || !tmdbType) return false

    if (storeTitleLogs.isWatchlistedByTmdb(tmdbId, tmdbType)) return true

    if (storeLists === null) {
      storeLists = useStoreLists()
    }
    const watchlist = storeLists.getSystemListByType('watchlist')
    if (!watchlist?.id) return false
    return storeLists.items.some(
      (item) =>
        item.list_id === watchlist.id &&
        item.titles?.tmdb_id == tmdbId &&
        item.titles?.tmdb_type == tmdbType
    )
  })

  const isInProgress = computed(() => {
    if (usesLogSystem.value) {
      return historyState.value === 'in_progress'
    }
    return false // Legacy system doesn't have in_progress
  })

  // Check if the current user has reviewed this title
  const isReviewed = computed(() => {
    if (storeReviews === null) {
      storeReviews = useStoreReviews()
    }
    if (storeAuth === null) {
      storeAuth = useStoreAuth()
    }

    const userId = storeAuth.user?.id
    const tmdbId = props.value?.title?.id

    if (!userId || !tmdbId) return false

    const userReviews = storeReviews.getReviewsByUserForTitle(userId, tmdbId)
    return userReviews && userReviews.length > 0
  })

  const userTitleInfoComputed = computed(() => {
    if (props.value?.userTitleInfo) return props.value.userTitleInfo
    return null
  })

  // Watch specific properties to detect in-place mutations on the same object ref
  watch(
    () => [
      userTitleInfoComputed.value?.is_seen,
      userTitleInfoComputed.value?.is_favourited,
      userTitleInfoComputed.value?.is_watchlisted
    ],
    ([seen, faved, watchlisted]) => {
      if (seen !== undefined) quickStateSeen.value = seen
      if (faved !== undefined) quickStateFaved.value = faved
      if (watchlisted !== undefined) quickStateWatchlist.value = watchlisted
    },
    { immediate: true }
  )

  return {
    openShareModal,
    openReviewCreateModal,
    openAddToListSheet,
    showAddToListSheet,
    showReviewSheet,
    addToListMetadata,
    toggleSeen,
    toggleFaved,
    toggleWatchlist,
    toggleInProgress,
    isSeen,
    isFaved,
    isWatchlisted,
    isInProgress,
    isReviewed,
    usesLogSystem,
    derivedState,
    historyState,
    quickStateSeen,
    quickStateFaved,
    quickStateWatchlist,
    quickStateInProgress
  }
}
